/**
 * Internal Observatory signals contracts — the one typed read surface (R16).
 *
 * `/internal/observatory/signals` serves agents and people through the same
 * shapes: `$OBS signals` verbs, `pnpm sj signals` verbs (read verbs only) and
 * Scotty's daily review all parse these. The endpoints are U12
 * (`apps/backend/src/api/_internal/observatory/signals/`).
 *
 * TWO KEYS. Reads, `link-ticket` and agent labels take `INTERNAL_API_KEY`.
 * Human-authority writes (a human label, a label acknowledgement, a
 * suppression rule, the config flip) take `OBSERVATORY_HUMAN_API_KEY` and
 * nothing else, so no agent holding the shared key can make them.
 *
 * THE EFFECTIVE CLASSIFICATION IS COMPUTED AT READ TIME, never stored: human
 * label beats acknowledged agent label beats policy. The stored policy values
 * stay on the signal unchanged, so a label never rewrites history.
 *
 * Plan: docs/plans/2026-09-26-1745-feat-observatory-signal-triage-pipeline-plan.md (U1, U12)
 */

import { z } from 'zod'

import {
  ObservatoryActorIdSchema,
  ObservatoryActorTypeSchema,
  ObservatoryCauseSchema,
  ObservatoryInvestigationStatusSchema,
  ObservatoryIssueSchema,
  ObservatorySeveritySchema,
  ObservatorySignalClassificationSchema,
  ObservatorySignalLabelInputSchema,
  ObservatorySignalLabelSchema,
  ObservatorySignalRunSchema,
  ObservatorySignalSchema,
  ObservatorySignalSourceSchema,
  ObservatorySignalStateSchema,
  ObservatorySignalTriageCapsSchema,
  ObservatorySignalTriageConfigSchema,
  ObservatorySignalTriageModeSchema,
  ObservatorySuppressionRuleSchema,
  ObservatoryTicketStateSchema
} from '../../../schemas/entities/observatory-signal'

const limitQuery = (max: number, fallback: number) =>
  z.coerce.number().int().min(1).max(max).optional().prefault(fallback)

// =============================================================================
// Effective classification
// =============================================================================

/** Which layer decided an effective value. `agent` appears only for an acknowledged agent label. */
export const OBSERVATORY_EFFECTIVE_SOURCES = ['human', 'agent', 'policy'] as const
export const ObservatoryEffectiveSourceSchema = z.enum(OBSERVATORY_EFFECTIVE_SOURCES)
export type ObservatoryEffectiveSource = z.infer<typeof ObservatoryEffectiveSourceSchema>

export const ObservatoryEffectiveClassificationSchema = z.object({
  state: ObservatorySignalStateSchema,
  severity: ObservatorySeveritySchema.nullable(),
  cause: ObservatoryCauseSchema,
  /** Per field, which layer won. */
  decidedBy: z.object({
    state: ObservatoryEffectiveSourceSchema,
    severity: ObservatoryEffectiveSourceSchema,
    cause: ObservatoryEffectiveSourceSchema
  })
})
export type ObservatoryEffectiveClassification = z.infer<typeof ObservatoryEffectiveClassificationSchema>

// =============================================================================
// list
// =============================================================================

/** `GET /internal/observatory/signals` — filters apply to the effective values. */
export const ListObservatorySignalsQuerySchema = z.object({
  state: ObservatorySignalStateSchema.optional(),
  severity: ObservatorySeveritySchema.optional(),
  source: ObservatorySignalSourceSchema.optional(),
  issueKey: z.string().min(1).max(191).optional(),
  /** Only signals last seen at or after this instant. */
  since: z.iso.datetime().optional(),
  limit: limitQuery(500, 100)
})
export type ListObservatorySignalsQuery = z.infer<typeof ListObservatorySignalsQuerySchema>

export const ObservatorySignalListItemSchema = z.object({
  signal: ObservatorySignalSchema,
  effective: ObservatoryEffectiveClassificationSchema
})
export type ObservatorySignalListItem = z.infer<typeof ObservatorySignalListItemSchema>

/** `total` counts every match; it can exceed `signals.length` when `limit` truncates. */
export const ListObservatorySignalsResponseSchema = z.object({
  signals: z.array(ObservatorySignalListItemSchema),
  total: z.number().int().nonnegative()
})
export type ListObservatorySignalsResponse = z.infer<typeof ListObservatorySignalsResponseSchema>

// =============================================================================
// show
// =============================================================================

/**
 * The latest investigation for the Issue's current episode. `packet` is left
 * open here: its schema is the investigate agent's output format (U10,
 * `investigation-packet.ts`), and it is stored post-checked and PII-scrubbed.
 */
export const ObservatoryInvestigationSummarySchema = z.object({
  id: z.string().min(1),
  episode: z.number().int().min(1),
  status: ObservatoryInvestigationStatusSchema,
  packet: z.record(z.string(), z.unknown()).nullable(),
  costUsd: z.number().nonnegative().nullable(),
  startedAt: z.iso.datetime(),
  finishedAt: z.iso.datetime().nullable()
})
export type ObservatoryInvestigationSummary = z.infer<typeof ObservatoryInvestigationSummarySchema>

/**
 * `GET /internal/observatory/signals/:id` — `:id` is the signal's public id or
 * its fingerprint. The policy trace rides on `signal.policyTrace`.
 */
export const ShowObservatorySignalResponseSchema = z.object({
  signal: ObservatorySignalSchema,
  effective: ObservatoryEffectiveClassificationSchema,
  issue: ObservatoryIssueSchema.nullable(),
  /** Labels on the signal and on its Issue, oldest first. Unacknowledged agent labels are included. */
  labels: z.array(ObservatorySignalLabelSchema),
  /** Every Jev call on this signal, newest first, with its raw answer (R8). */
  classifications: z.array(ObservatorySignalClassificationSchema),
  investigation: ObservatoryInvestigationSummarySchema.nullable()
})
export type ShowObservatorySignalResponse = z.infer<typeof ShowObservatorySignalResponseSchema>

// =============================================================================
// history
// =============================================================================

/** `GET /internal/observatory/signals/:id/history` — per-tick observations, 30-day retention (KTD7). */
export const ObservatorySignalHistoryQuerySchema = z.object({
  since: z.iso.datetime().optional(),
  limit: limitQuery(720, 168)
})
export type ObservatorySignalHistoryQuery = z.infer<typeof ObservatorySignalHistoryQuerySchema>

/** One tick's facts plus the classification the signal had at that tick (the R8 history). */
export const ObservatorySignalObservationSchema = z.object({
  runId: z.string().min(1),
  observedAt: z.iso.datetime(),
  facts: z.record(z.string(), z.unknown()),
  state: ObservatorySignalStateSchema,
  severity: ObservatorySeveritySchema.nullable(),
  cause: ObservatoryCauseSchema,
  jevModel: z.string().nullable()
})
export type ObservatorySignalObservation = z.infer<typeof ObservatorySignalObservationSchema>

/** Newest first. */
export const ObservatorySignalHistoryResponseSchema = z.object({
  fingerprint: z.string().min(1),
  observations: z.array(ObservatorySignalObservationSchema)
})
export type ObservatorySignalHistoryResponse = z.infer<typeof ObservatorySignalHistoryResponseSchema>

// =============================================================================
// aging
// =============================================================================

/** `GET /internal/observatory/signals/aging` — high or critical Issues open `minDays` or more. */
export const ObservatorySignalAgingQuerySchema = z.object({
  minDays: z.coerce.number().int().min(0).max(365).optional().prefault(3),
  /** Only Issues with no human touch on their ticket (or no ticket at all). */
  untouched: z.stringbool().optional().default(false)
})
export type ObservatorySignalAgingQuery = z.infer<typeof ObservatorySignalAgingQuerySchema>

export const ObservatoryAgingIssueSchema = z.object({
  issue: ObservatoryIssueSchema,
  /** Whole days since the current episode started. */
  ageDays: z.number().int().nonnegative(),
  /** Null when no human has touched the linked ticket, or there is none. */
  lastHumanTouchAt: z.iso.datetime().nullable(),
  signalCount: z.number().int().nonnegative()
})
export type ObservatoryAgingIssue = z.infer<typeof ObservatoryAgingIssueSchema>

/** Oldest first. */
export const ObservatorySignalAgingResponseSchema = z.object({
  issues: z.array(ObservatoryAgingIssueSchema)
})
export type ObservatorySignalAgingResponse = z.infer<typeof ObservatorySignalAgingResponseSchema>

// =============================================================================
// runs
// =============================================================================

/** `GET /internal/observatory/signals/runs`. */
export const ListObservatorySignalRunsQuerySchema = z.object({
  limit: limitQuery(200, 24)
})
export type ListObservatorySignalRunsQuery = z.infer<typeof ListObservatorySignalRunsQuerySchema>

/**
 * Newest first. `staleSources` names every source with no completed run in the
 * last 2 hours — distinct from a source that ran `ok` and emitted 0 signals.
 */
export const ListObservatorySignalRunsResponseSchema = z.object({
  runs: z.array(ObservatorySignalRunSchema),
  staleSources: z.array(ObservatorySignalSourceSchema)
})
export type ListObservatorySignalRunsResponse = z.infer<typeof ListObservatorySignalRunsResponseSchema>

// =============================================================================
// labels
// =============================================================================

/** `GET /internal/observatory/signals/labels/export` — evaluation data. */
export const ExportObservatorySignalLabelsQuerySchema = z.object({
  /** Exclude every agent label, acknowledged or not. */
  humanOnly: z.stringbool().optional().default(false),
  actorType: ObservatoryActorTypeSchema.optional(),
  since: z.iso.datetime().optional()
})
export type ExportObservatorySignalLabelsQuery = z.infer<typeof ExportObservatorySignalLabelsQuerySchema>

export const ExportObservatorySignalLabelsResponseSchema = z.object({
  labels: z.array(ObservatorySignalLabelSchema)
})
export type ExportObservatorySignalLabelsResponse = z.infer<typeof ExportObservatorySignalLabelsResponseSchema>

/**
 * `POST /internal/observatory/signals/labels` — append-only. The server, not
 * the body, has the last word on `actorType`: a write on the agent key is
 * stored as `agent` whatever the body says (U12).
 */
export const CreateObservatorySignalLabelRequestSchema = ObservatorySignalLabelInputSchema
export type CreateObservatorySignalLabelRequest = z.infer<typeof CreateObservatorySignalLabelRequestSchema>

export const CreateObservatorySignalLabelResponseSchema = z.object({
  label: ObservatorySignalLabelSchema
})
export type CreateObservatorySignalLabelResponse = z.infer<typeof CreateObservatorySignalLabelResponseSchema>

/** `POST /internal/observatory/signals/labels/:labelId/ack` — human-authority key only. */
export const AckObservatorySignalLabelRequestSchema = z.object({
  acknowledgedBy: ObservatoryActorIdSchema
})
export type AckObservatorySignalLabelRequest = z.infer<typeof AckObservatorySignalLabelRequestSchema>

export const AckObservatorySignalLabelResponseSchema = z.object({
  label: ObservatorySignalLabelSchema
})
export type AckObservatorySignalLabelResponse = z.infer<typeof AckObservatorySignalLabelResponseSchema>

// =============================================================================
// link-ticket
// =============================================================================

/**
 * `POST /internal/observatory/signals/link-ticket` — attach an existing ticket
 * to an Issue (the go-live ticket-link import, R19). The issueKey rides in the
 * body because it can contain `/`. Re-linking the same ticket is a no-op; a
 * different one is refused unless `replace`, which moves the old ref into
 * `priorTickets` for the current episode.
 */
export const LinkObservatoryIssueTicketRequestSchema = z.object({
  issueKey: z.string().min(1).max(191),
  ticketRef: z.string().regex(/^[A-Z][A-Z0-9]*-\d+$/, 'a Linear identifier such as SCH-2306'),
  ticketState: ObservatoryTicketStateSchema.optional(),
  replace: z.boolean().optional().default(false)
})
export type LinkObservatoryIssueTicketRequest = z.infer<typeof LinkObservatoryIssueTicketRequestSchema>

export const LinkObservatoryIssueTicketResponseSchema = z.object({
  issue: ObservatoryIssueSchema,
  /** False when the Issue already carried this ticket. */
  changed: z.boolean()
})
export type LinkObservatoryIssueTicketResponse = z.infer<typeof LinkObservatoryIssueTicketResponseSchema>

// =============================================================================
// suppression rules
// =============================================================================

/** `GET /internal/observatory/signals/suppression-rules`. */
export const ListObservatorySuppressionRulesResponseSchema = z.object({
  rules: z.array(ObservatorySuppressionRuleSchema)
})
export type ListObservatorySuppressionRulesResponse = z.infer<typeof ListObservatorySuppressionRulesResponseSchema>

/**
 * `POST /internal/observatory/signals/suppression-rules` — human-authority key
 * only. Upserts by `ruleKey`, so re-running a seed creates no duplicates and a
 * re-seeded rule keeps its original `createdAt`. `match` must parse as the
 * pure core's match spec, or the rule would silently suppress nothing.
 */
export const CreateObservatorySuppressionRuleRequestSchema = ObservatorySuppressionRuleSchema.pick({
  ruleKey: true,
  match: true,
  owner: true,
  reason: true
}).extend({
  createdBy: ObservatoryActorIdSchema,
  expiresAt: z.iso.datetime().nullable().optional().default(null)
})
export type CreateObservatorySuppressionRuleRequest = z.infer<typeof CreateObservatorySuppressionRuleRequestSchema>

export const CreateObservatorySuppressionRuleResponseSchema = z.object({
  rule: ObservatorySuppressionRuleSchema,
  /** False when a rule with this `ruleKey` already existed and was updated in place. */
  created: z.boolean()
})
export type CreateObservatorySuppressionRuleResponse = z.infer<typeof CreateObservatorySuppressionRuleResponseSchema>

/**
 * `POST /internal/observatory/signals/suppression-rules/seed` — human-authority
 * key only. Puts the R10 noise rules (the pure core's
 * `SEED_SUPPRESSION_RULES`) through the same upsert as a single rule, so a
 * re-run reports `created: false` for each and writes no duplicate.
 */
export const SeedObservatorySuppressionRulesRequestSchema = z.object({
  createdBy: ObservatoryActorIdSchema
})
export type SeedObservatorySuppressionRulesRequest = z.infer<typeof SeedObservatorySuppressionRulesRequestSchema>

export const SeedObservatorySuppressionRulesResponseSchema = z.object({
  seeded: z.array(z.object({ ruleKey: z.string().min(1), created: z.boolean() }))
})
export type SeedObservatorySuppressionRulesResponse = z.infer<typeof SeedObservatorySuppressionRulesResponseSchema>

// =============================================================================
// config (the kill switch, KTD13)
// =============================================================================

/** `GET /internal/observatory/signals/config`. `stored: false` is the shadow default. */
export const ObservatorySignalTriageConfigResponseSchema = z.object({
  config: ObservatorySignalTriageConfigSchema,
  stored: z.boolean(),
  updatedAt: z.iso.datetime().nullable(),
  updatedBy: z.string().nullable()
})
export type ObservatorySignalTriageConfigResponse = z.infer<typeof ObservatorySignalTriageConfigResponseSchema>

/**
 * `POST /internal/observatory/signals/config` — human-authority key only. Caps
 * not named keep their current value.
 */
export const SetObservatorySignalTriageConfigRequestSchema = z.object({
  mode: ObservatorySignalTriageModeSchema,
  // Not `.partial()` of the caps schema: its fields carry defaults, which Zod 4
  // applies inside an optional, so an unnamed cap would reset to its default.
  caps: z
    .object({
      newTicketsPerTick: z.number().int().min(0).optional(),
      perIssuePerHour: z.number().int().min(0).optional(),
      perDay: z.number().int().min(0).optional()
    } satisfies Record<keyof z.infer<typeof ObservatorySignalTriageCapsSchema>, z.ZodType>)
    .optional(),
  updatedBy: ObservatoryActorIdSchema
})
export type SetObservatorySignalTriageConfigRequest = z.infer<typeof SetObservatorySignalTriageConfigRequestSchema>
